import { useMemo } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Badge,
  Button,
  ButtonText,
  Card,
  DetailRow,
  EmptyState,
  ProtectedScreen,
  SearchableSelect,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import { PlantCareReminders } from "~/features/plants/PlantCareReminders";
import { PlantJournal } from "~/features/plants/PlantJournal";
import { PlantPhotos } from "~/features/plants/PlantPhotos";
import { useCollections } from "~/state/collections";
import { usePlant, usePlantActions } from "~/state/plants";
import { RANK_LABELS, useTaxonLineage } from "~/state/taxonomy";
import { spacing } from "~/styles/theme";
import type {
  PlantAcquisition,
  PlantPlacement,
  PlantStatus,
} from "~/api/types";

const statusOptions = [
  { value: "thriving", label: "Thriving" },
  { value: "stable", label: "Doing fine" },
  { value: "struggling", label: "Struggling" },
  { value: "dormant", label: "Dormant" },
  { value: "dead", label: "Died" },
  { value: "gifted", label: "Gifted away" },
  { value: "sold", label: "Sold" },
];

const placementOptions = [
  { value: "pot", label: "In a pot" },
  { value: "ground", label: "In the ground" },
  { value: "raised_bed", label: "Raised bed" },
  { value: "hanging", label: "Hanging" },
  { value: "terrarium", label: "Terrarium" },
  { value: "hydroponic", label: "Hydroponic" },
  { value: "greenhouse", label: "Greenhouse" },
];

const acquisitionOptions = [
  { value: "nursery", label: "Nursery" },
  { value: "garden_centre", label: "Garden centre" },
  { value: "gift", label: "Gift" },
  { value: "propagation", label: "Propagated" },
  { value: "seed", label: "Grown from seed" },
  { value: "trade", label: "Trade or swap" },
  { value: "wild_collected", label: "Wild collected" },
  { value: "inherited", label: "Inherited" },
  { value: "unknown", label: "Not sure" },
];

export default function PlantDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const plant = usePlant(id);
  const collections = useCollections();
  const actions = usePlantActions();
  const lineage = useTaxonLineage(plant?.taxon_id);

  const collectionOptions = useMemo(
    () =>
      collections.map((collection) => ({
        value: collection.id,
        label: collection.name,
      })),
    [collections],
  );

  if (!plant) {
    return (
      <ProtectedScreen narrow title="Plant">
        <EmptyState
          title="Plant not found"
          description="It may have been deleted on another device."
        />
      </ProtectedScreen>
    );
  }

  const collection = collections.find(
    (candidate) => candidate.id === plant.collection_id,
  );
  const isPlaced = plant.map_lat !== null || plant.map_x !== null;

  return (
    <ProtectedScreen
      narrow
      title={plant.display_name}
      subtitle={collection?.name ? `In ${collection.name}` : undefined}
    >
      <Card gap="md">
        <SectionHeader title="Botanical classification" />
        {plant.taxon_id ? (
          lineage.data && lineage.data.length > 0 ? (
            <Stack gap="xs">
              {lineage.data.map((entry) => (
                <DetailRow
                  key={entry.id}
                  label={RANK_LABELS[entry.rank]}
                  value={entry.name}
                />
              ))}
            </Stack>
          ) : (
            <AppText size="xs">
              {lineage.isLoading
                ? "Loading classification..."
                : "Classification unavailable offline."}
            </AppText>
          )
        ) : (
          <EmptyState
            title="Not identified yet"
            description="Search the botanical index from the collection screen to link this plant to a species."
          />
        )}
      </Card>

      <Card gap="md">
        <SectionHeader title="Photos" />
        <PlantPhotos
          plantId={plant.id}
          coverPhotoPath={plant.cover_photo_path}
        />
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Care reminders"
          description="Water, feed, and repot on a schedule."
        />
        <PlantCareReminders plantId={plant.id} />
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Journal"
          description="A running log of care and observations."
        />
        <PlantJournal plantId={plant.id} />
      </Card>

      <Card gap="md">
        <SectionHeader title="Details" />
        <View style={stylesheet.grid}>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Nickname
            </AppText>
            <TextField
              accessibilityLabel="Nickname"
              value={plant.nickname ?? ""}
              placeholder="The big one by the window"
              onChangeText={(value) =>
                actions.update(plant.id, { nickname: value })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Collection
            </AppText>
            <SearchableSelect
              title="Move to a collection"
              accessibilityLabel="Collection"
              value={plant.collection_id}
              options={collectionOptions}
              placeholder="Choose a collection"
              onValueChange={(value) =>
                actions.update(plant.id, { collectionId: value })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              How's it doing?
            </AppText>
            <SearchableSelect
              title="Choose a status"
              accessibilityLabel="Status"
              value={plant.status}
              options={statusOptions}
              placeholder="Doing fine"
              onValueChange={(value) =>
                actions.update(plant.id, { status: value as PlantStatus })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Where does it live?
            </AppText>
            <SearchableSelect
              title="Choose a placement"
              accessibilityLabel="Placement"
              value={plant.placement}
              options={placementOptions}
              placeholder="In a pot"
              onValueChange={(value) =>
                actions.update(plant.id, { placement: value as PlantPlacement })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Where did it come from?
            </AppText>
            <SearchableSelect
              title="Choose a source"
              accessibilityLabel="Source"
              value={plant.acquisition}
              options={acquisitionOptions}
              placeholder="Not sure"
              onValueChange={(value) =>
                actions.update(plant.id, {
                  acquisition: value as PlantAcquisition,
                })
              }
            />
          </View>
          <View style={stylesheet.field}>
            <AppText size="xs" weight="semibold">
              Acquired from
            </AppText>
            <TextField
              accessibilityLabel="Acquired from"
              value={plant.acquired_from ?? ""}
              placeholder="Kings Plant Barn"
              onChangeText={(value) =>
                actions.update(plant.id, { acquiredFrom: value })
              }
            />
          </View>
        </View>

        <View style={stylesheet.fullField}>
          <AppText size="xs" weight="semibold">
            Notes
          </AppText>
          <TextField
            accessibilityLabel="Notes"
            value={plant.notes ?? ""}
            placeholder="Repotted in spring, prefers morning sun"
            multiline
            style={stylesheet.textarea}
            onChangeText={(value) => actions.update(plant.id, { notes: value })}
          />
        </View>
      </Card>

      <Card gap="md">
        <SectionHeader
          title="Map position"
          description={
            collection?.map_kind === "none"
              ? "This collection doesn't have a map turned on yet."
              : undefined
          }
        />
        <View style={stylesheet.mapRow}>
          <Badge tone={isPlaced ? "success" : "neutral"}>
            {isPlaced ? "Placed on the map" : "Not placed"}
          </Badge>
          {collection && collection.map_kind !== "none" ? (
            <Button
              compact
              variant="outline"
              iconName="map-pin"
              onPress={() =>
                router.push(`/collections/${collection.id}/map` as never)
              }
            >
              <ButtonText>{isPlaced ? "View on map" : "Place it"}</ButtonText>
            </Button>
          ) : null}
          {isPlaced ? (
            <Button
              compact
              variant="outline"
              onPress={() => actions.place(plant.id, { kind: "clear" })}
            >
              <ButtonText>Remove pin</ButtonText>
            </Button>
          ) : null}
        </View>
      </Card>

      <Card gap="sm" danger>
        <SectionHeader title="Remove this plant" />
        <Button
          variant="danger"
          align="start"
          iconName="trash-2"
          onPress={() => {
            actions.remove(plant.id);
            router.replace("/plants" as never);
          }}
        >
          <ButtonText>Delete plant</ButtonText>
        </Button>
      </Card>
    </ProtectedScreen>
  );
}

const stylesheet = StyleSheet.create({
  grid: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
  field: { minWidth: 220, flex: 1, gap: spacing.xs },
  fullField: { gap: spacing.xs },
  textarea: { minHeight: 86, textAlignVertical: "top" },
  mapRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: spacing.s,
  },
});
